import React, { useState } from "react";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import { FiPlusCircle } from "react-icons/fi";
import VendorModal from "./VendorModal";
import { useItemsFilter } from "../../_helper/actions/app/items";
import { ApiEndPoint } from "../../_helper/constant/api-types";
import { useAtom } from "jotai";
import { clinicAtom, pharmacyAtom } from "../../_helper/constant/constant-atom";
import { useDeleteItem } from "../../_helper/actions/mutation/items";
import { toast } from "react-toastify";

const VendorMaster = () => {
  const [showModal, setShowModal] = useState(false);
  const [editVendor, setEditVendor] = useState(null);
  const [clinic] = useAtom(clinicAtom);
  const [pharamcy] = useAtom(pharmacyAtom);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5); // Default number of rows per page

  const { data: vendors } = useItemsFilter(
    ApiEndPoint.INVENTORY,
    `?action=getVendor&${clinic.id ? `clinic_id=${clinic.id}` : `pharmacy_id=${pharamcy.id}`}`
  );

  const delete_mutation = useDeleteItem(ApiEndPoint.INVENTORY);

  const handleAddNew = () => {
    setEditVendor(null);
    setShowModal(true);
  };

  const handleEdit = (vendor) => {
    setEditVendor(vendor);
    setShowModal(true);
  };

  const confirmDelete = (vendor) => {
    setVendorToDelete(vendor);
    setShowConfirmation(true);
  };

  const handleDelete = () => {
    delete_mutation.mutate(
      {
        data: {
          action: "delVendor",
          id: vendorToDelete.id,
        },
      },
      {
        onSuccess: () => {
          toast.success("Vendor Deleted!");
          setShowConfirmation(false);
          setVendorToDelete(null);
        },
      }
    );
  };

  // Pagination Logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentVendors = vendors?.slice(indexOfFirstRow, indexOfLastRow) || [];

  const totalPages = Math.ceil((vendors?.length || 0) / rowsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1); // Reset to the first page when rowsPerPage changes
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      {/* Header Section */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
          Vendor Master
        </h2>
        <button
          onClick={handleAddNew}
          className="flex items-center gap-2 px-4 sm:px-6 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition duration-200"
        >
          <FiPlusCircle size={20} />
          <span className="text-sm sm:text-base">Add New Vendor</span>
        </button>
      </div>

      {/* Vendor Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left border-collapse">
            <thead className="bg-gray-200 text-gray-700 text-sm">
              <tr>
                <th className="p-4 font-semibold">Vendor Name</th>
                <th className="p-4 font-semibold">Type</th>
                <th className="p-4 font-semibold">GSTIN</th>
                <th className="p-4 font-semibold">Contact Person Name</th>
                <th className="p-4 font-semibold">Contact Person Phone</th>
                <th className="p-4 font-semibold">Contact Person Email</th>
                <th className="p-4 font-semibold text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentVendors.map((vendor) => (
                <tr
                  key={vendor.id}
                  className="hover:bg-gray-100 border-b border-gray-300"
                >
                  <td className="p-4">{vendor.vendor_name}</td>
                  <td className="p-4">{vendor.type}</td>
                  <td className="p-4">{vendor.gstin}</td>
                  <td className="p-4">{vendor.contact_person_name}</td>
                  <td className="p-4">{vendor.contact_phone}</td>
                  <td className="p-4">{vendor.contact_email}</td>
                  <td className="p-4 flex justify-center gap-4 text-blue-600">
                    <button
                      onClick={() => handleEdit(vendor)}
                      className="flex items-center gap-1 hover:text-blue-800"
                    >
                      <FaEdit />
                      Edit
                    </button>
                    <button
                      onClick={() => confirmDelete(vendor)}
                      className="flex items-center gap-1 text-red-600 hover:text-red-800"
                    >
                      <FaTrashAlt />
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {vendors?.length === 0 && (
                <tr>
                  <td
                    colSpan="7"
                    className="p-6 text-center text-gray-500 bg-gray-100"
                  >
                    No vendors found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      <div className="mt-4 flex flex-col sm:flex-row justify-between items-center gap-4">
        {/* Rows Per Page Selector */}
        <div className="flex items-center gap-2">
          <span className="text-gray-600">Rows per page:</span>
          <select
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
            className="px-3 py-1 border rounded-md text-gray-700 bg-white shadow-sm focus:outline-none"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>
        </div>

        {/* Page Numbers */}
        <div className="flex items-center gap-2">
          {[...Array(totalPages).keys()].map((number) => (
            <button
              key={number}
              onClick={() => handlePageChange(number + 1)}
              className={`px-3 py-1 rounded-md ${
                currentPage === number + 1
                  ? "bg-green-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              {number + 1}
            </button>
          ))}
        </div>
      </div>

      {/* Vendor Modal */}
      {showModal && (
        <VendorModal onClose={() => setShowModal(false)} vendor={editVendor} />
      )}

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-lg max-w-md w-full">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">
              Confirm Delete
            </h3>
            <p className="text-gray-600">
              Are you sure you want to delete{" "}
              <span className="font-bold">{vendorToDelete?.name}</span>?
            </p>
            <div className="mt-6 flex justify-end gap-4">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorMaster;
